import { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Volume2, Play, Pause, Square, SkipBack, SkipForward, Loader2, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getBackendUrl, getApiUrl } from '../config/environment';
// 🛑 Iter338 : Bus audio centralisé (stop sur changement de route)
import OwenAudioBus from '../services/owenAudioBus';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

/**
 * Iter295 — Cache module-niveau persistant entre re-mounts.
 * Map<moduleId, { blobUrl, durationMs }> — survivant à la navigation back/forward
 * dans la même session JS. Les blob URLs sont révoquées uniquement quand le
 * module en lui-même est remplacé (jamais sur unmount d'un composant donné).
 */
const audioBlobCache = new Map();

/**
 * ModuleAudioPlayer — Lecteur audio LECTURE SEULE.
 *
 * - Appelle GET /audio-status (jamais POST /speak).
 * - Ne déclenche JAMAIS de génération.
 * - Iter295 : préchargement en arrière-plan + cache module-niveau →
 *   bouton Play visible IMMÉDIATEMENT dès la réponse /audio-status,
 *   le blob audio est téléchargé en parallèle de l'affichage UI sans
 *   bloquer le rendu. Le 2e accès (re-mount) est servi depuis le cache.
 */
const ModuleAudioPlayer = ({ moduleId, moduleTitle, onStatusChange }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | playing | paused | stopped | not_available | processing
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [audioData, setAudioData] = useState(null);
  // Iter295 — flag de préchargement en background (ne bloque PAS l'UI)
  const [isPreloading, setIsPreloading] = useState(false);

  const audioRef = useRef(null);
  const wakeLockRef = useRef(null);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateStatus = (newStatus) => {
    setStatus(newStatus);
    if (onStatusChange) onStatusChange(newStatus);
  };

  // ─── Vérifier la disponibilité (GET, lecture seule, jamais 404) ───
  const checkAudioAvailability = async () => {
    try {
      // Iter295 — pas de status='loading' ici. /audio-status est rapide
      // (~50-200ms) et le bouton Play doit être visible quasi-immédiatement.
      const response = await axios.get(`${API}/modules-v2/${moduleId}/audio-status`);
      const data = response.data;

      if (data.status === 'available' && data.audio_url) {
        let audioUrl = data.audio_url;
        if (audioUrl.startsWith('/')) {
          audioUrl = `${BACKEND_URL}${audioUrl}`;
        }
        setAudioData({
          audio_url: audioUrl,
          duration_ms: data.duration_ms || 0,
          module_title: data.module_title || moduleTitle,
        });
        updateStatus('idle');
      } else if (data.status === 'processing') {
        updateStatus('processing');
      } else {
        updateStatus('not_available');
      }
    } catch {
      // Même en cas d'erreur réseau, on affiche "non disponible" sans 404
      updateStatus('not_available');
    }
  };

  // ─── Iter295 — Préchargement audio en ARRIÈRE-PLAN (ne bloque pas l'UI) ───
  useEffect(() => {
    if (!audioData?.audio_url || audioRef.current) return;

    // 1. Cache hit : ré-utiliser le blob URL existant → 0 latence
    const cached = audioBlobCache.get(String(moduleId));
    if (cached?.blobUrl) {
      const audio = OwenAudioBus.createAudio(cached.blobUrl, { label: `module-${moduleId}-cached` });
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
      audio.addEventListener('timeupdate', () => {
        const dur = audio.duration || 1;
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / dur) * 100);
      });
      audio.addEventListener('ended', () => {
        updateStatus('stopped');
        setCurrentTime(0);
        setProgress(0);
      });
      audio.addEventListener('error', () => updateStatus('not_available'));
      if ('mediaSession' in navigator && moduleTitle) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: moduleTitle,
          artist: 'Owen – Coach DIA',
          album: 'Formation Coach DIA',
        });
      }
      // Cache hit : on ne touche PAS à status (déjà 'idle' = bouton Play visible)
      return;
    }

    // 2. Cache miss : préchargement EN ARRIÈRE-PLAN.
    // Le bouton Play reste visible et cliquable IMMÉDIATEMENT (status='idle').
    // Si l'utilisateur clique avant que le blob soit prêt, handlePlayPause
    // affichera un mini-spinner localisé sur le bouton.
    let cancelled = false;
    setIsPreloading(true);

    (async () => {
      try {
        const response = await axios.get(audioData.audio_url, {
          responseType: 'blob',
          timeout: 30000,
        });

        if (cancelled) return;

        const blobUrl = URL.createObjectURL(response.data);
        audioBlobCache.set(String(moduleId), {
          blobUrl,
          durationMs: audioData.duration_ms,
        });

        const audio = OwenAudioBus.createAudio(blobUrl, { label: `module-${moduleId}-fresh` });
        audioRef.current = audio;

        audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
        audio.addEventListener('timeupdate', () => {
          const dur = audio.duration || 1;
          setCurrentTime(audio.currentTime);
          setProgress((audio.currentTime / dur) * 100);
        });
        audio.addEventListener('ended', () => {
          updateStatus('stopped');
          setCurrentTime(0);
          setProgress(0);
        });
        audio.addEventListener('error', () => updateStatus('not_available'));

        if ('mediaSession' in navigator && moduleTitle) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: moduleTitle,
            artist: 'Owen – Coach DIA',
            album: 'Formation Coach DIA',
          });
        }
      } catch {
        if (!cancelled) updateStatus('not_available');
      } finally {
        if (!cancelled) setIsPreloading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Iter295 — on ne révoque PAS le blobUrl ici (cache module-niveau).
      // L'audio HTMLElement local est mis en pause sans casser le cache.
      if (audioRef.current) {
        audioRef.current.pause();
        // Iter338 : unregister du bus (le bus pause aussi mais on est explicite)
        try { OwenAudioBus.unregister(audioRef.current); } catch (_) { /* noop */ }
        audioRef.current = null;
      }
      releaseWakeLock();
    };
  }, [audioData, moduleId, moduleTitle]); // eslint-disable-line

  // ─── Charger au montage ───
  useEffect(() => {
    if (moduleId) checkAudioAvailability();
  }, [moduleId]); // eslint-disable-line

  // ─── WakeLock ───
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch { /* non critique */ }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch { /* ignore */ }
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    if (status === 'playing') requestWakeLock();
    else releaseWakeLock();
  }, [status]);

  // ─── Contrôles audio ───
  const handlePlayPause = useCallback(() => {
    // Iter295 — Si l'utilisateur clique avant que le préchargement soit fini,
    // on attend en silence (le bouton affiche un mini-spinner via isPreloading).
    if (!audioRef.current) return;
    if (status === 'playing') {
      audioRef.current.pause();
      updateStatus('paused');
    } else {
      audioRef.current.play()
        .then(() => updateStatus('playing'))
        .catch(() => updateStatus('not_available'));
    }
  }, [status]);

  const handleStop = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    setProgress(0);
    updateStatus('stopped');
  }, []);

  const handleSkip = useCallback((seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + seconds));
  }, []);

  const handleSeek = useCallback((value) => {
    if (!audioRef.current?.duration) return;
    audioRef.current.currentTime = (value[0] / 100) * audioRef.current.duration;
  }, []);

  // ─── Ne rien rendre si audio non disponible ───
  if (status === 'not_available') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
        <Card
          data-testid="audio-player-not-available"
          className="border border-gray-200/50 overflow-hidden mb-8"
          style={{ background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(12px)' }}
        >
          <CardContent className="py-6 px-6 flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Volume2 className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500" data-testid="audio-not-available-msg">
              Audio non encore disponible pour ce module.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (status === 'processing') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
        <Card
          data-testid="audio-player-processing"
          className="border border-amber-200/50 overflow-hidden mb-8"
          style={{ background: 'rgba(255,251,235,0.7)', backdropFilter: 'blur(12px)' }}
        >
          <CardContent className="py-6 px-6 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500 animate-pulse" />
            <p className="text-sm text-amber-700" data-testid="audio-processing-msg">
              L&apos;audio de ce module est en cours de préparation.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ─── Lecteur principal ───
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        data-testid="audio-player-card"
        className="border border-[#FFD86F]/15 overflow-hidden mb-8"
        style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 40px rgba(255,216,111,0.15), inset 0 1px 3px rgba(255,255,255,0.8)',
        }}
      >
        <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#FFD86F] to-[#FFB347] rounded-lg shadow-md">
              <Volume2 className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#2B1854]">
              Écouter ce module avec Owen
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Iter295 — Plus jamais d'overlay "Préparation de l'audio..." plein composant.
              Le bouton Play est visible immédiatement. Un mini-spinner localisé
              sur le bouton (audio-mini-spinner) apparaît si l'utilisateur clique
              avant la fin du préchargement en arrière-plan. */}
          {audioData && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 justify-center">
                <Button
                  data-testid="audio-play-pause-btn"
                  onClick={handlePlayPause}
                  size="lg"
                  className="rounded-full w-16 h-16 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #FFD86F 0%, #FFB347 100%)', color: '#1B0F3B' }}
                  aria-busy={isPreloading && !audioRef.current}
                >
                  {isPreloading && !audioRef.current ? (
                    <Loader2 className="w-6 h-6 animate-spin" data-testid="audio-mini-spinner" />
                  ) : status === 'playing' ? (
                    <Pause className="w-6 h-6" />
                  ) : (
                    <Play className="w-6 h-6 ml-1" />
                  )}
                </Button>

                <Button
                  data-testid="audio-stop-btn"
                  onClick={handleStop}
                  size="lg"
                  variant="outline"
                  className="rounded-full w-12 h-12"
                  disabled={status === 'idle' || status === 'stopped'}
                >
                  <Square className="w-5 h-5" />
                </Button>

                <Button onClick={() => handleSkip(-10)} variant="outline" className="gap-1" disabled={!audioData || status === 'idle'}>
                  <SkipBack className="w-4 h-4" /><span className="text-sm">10s</span>
                </Button>

                <Button onClick={() => handleSkip(10)} variant="outline" className="gap-1" disabled={!audioData || status === 'idle'}>
                  <span className="text-sm">10s</span><SkipForward className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Slider
                  value={[progress]}
                  max={100}
                  step={0.1}
                  onValueChange={handleSeek}
                  disabled={!audioData || status === 'idle'}
                  className="w-full"
                />
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span className="text-[#FFD86F] font-semibold">{Math.round(progress)}%</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ModuleAudioPlayer;
